import React from 'react';
import Box from '@mui/material/Box';
import { FdaSourceKey } from '../services/api/domains/products/types';
import {
  COLOR_INFO_TEXT,
  COLOR_INFO_BANNER_BG,
  COLOR_INFO_BANNER_BORDER,
} from 'theme/colors';
import { GRAY_500 } from 'theme/grayScale';
import {
  OVERVIEW_COHORT_LABELS,
  type OverviewCohortKey,
} from './metrics/overviewCohorts';

const SOURCE_LABELS: Record<FdaSourceKey, string> = {
  ob: 'Orange Book',
  ndc: 'NDC',
  dmf: 'USDMF',
};

export interface FdaSourceUnlinkedBannerProps {
  source: FdaSourceKey;
  /** 'unlinked' ("Not Yet Sourceable") or 'linked' ("Sourceable via
   * Navigator", human direction, 2026-09-28) -- selects the banner's own
   * text; the clear behavior is identical either way. */
  linkFilter: 'unlinked' | 'linked' | 'cohort';
  /** Metrics Overview ingredient cohort (docs/features/metrics-overview-
   * tab.md, Requirement 5); names the banner when linkFilter === 'cohort'. */
  cohort?: OverviewCohortKey;
  onClear: () => void;
}

const BANNER_TEXT: Record<'unlinked' | 'linked', string> = {
  unlinked: 'Viewing Not Yet Sourceable products from',
  linked: 'Viewing Sourceable via Navigator products from',
};

const bannerText = (
  linkFilter: FdaSourceUnlinkedBannerProps['linkFilter'],
  cohort: OverviewCohortKey | undefined
): string =>
  linkFilter === 'cohort'
    ? `Viewing ${cohort ? OVERVIEW_COHORT_LABELS[cohort] : 'Overview ingredients'} from`
    : BANNER_TEXT[linkFilter];

/**
 * Replaces the FDA Sources search bar/toggle/filter controls while a
 * Navigator-tab "Not Yet Sourceable"/"Sourceable via Navigator" metric card
 * deep link is active (docs/features/fda-source-record-browser.md, Task
 * FSU-004; `linked` added human direction, 2026-09-28). The X clears the
 * linked/unlinked-only filter, restoring normal FDA Sources search — it
 * does not navigate away or change the selected source.
 */
const FdaSourceUnlinkedBanner: React.FC<FdaSourceUnlinkedBannerProps> = ({
  source,
  linkFilter,
  cohort,
  onClear,
}) => (
  <div style={styles.banner} data-testid="fda-source-unlinked-banner">
    <span style={styles.text}>
      {bannerText(linkFilter, cohort)} {SOURCE_LABELS[source]}
    </span>
    <Box
      component="button"
      onClick={onClear}
      sx={{
        // All static values live here, not in a plain `style` object --
        // an inline `style` attribute always wins the cascade over a
        // pseudo-class rule from the same element's `sx`-generated class,
        // which would silently make the :hover/:active rules below inert
        // (Copilot review, PR #832; same root cause as the "LgmButton
        // background shorthand" issue this project has hit before).
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: GRAY_500,
        fontSize: '1.125rem',
        lineHeight: 1,
        padding: '0 0.25rem',
        borderRadius: '0.25rem',
        '&:hover': {
          color: COLOR_INFO_TEXT,
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&:active': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&:focus-visible': {
          outline: `2px solid ${COLOR_INFO_TEXT}`,
          outlineOffset: '1px',
        },
      }}
      aria-label={`Clear ${
        linkFilter === 'cohort' && cohort
          ? OVERVIEW_COHORT_LABELS[cohort]
          : linkFilter
      } filter and return to FDA Sources search`}
      title="Clear filter"
      data-testid="fda-source-unlinked-banner-close">
      ×
    </Box>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  banner: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    backgroundColor: COLOR_INFO_BANNER_BG,
    border: `1px solid ${COLOR_INFO_BANNER_BORDER}`,
    borderRadius: '0.375rem',
  },
  text: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    color: COLOR_INFO_TEXT,
  },
};

export default FdaSourceUnlinkedBanner;
